import type { ReactNode } from 'react'
import { Link2 } from 'lucide-react'
import type { AuditTrailRow } from '@/types/audit'
import {
  auditActorLabel,
  auditStorageNode,
  auditSourceLabel,
  renderAuditValue,
} from '@/lib/auditTrail'
import {
  linkLabel,
  linkTarget,
  productTarget,
  storageTarget,
  subjectTarget,
  submissionTarget,
} from '@/lib/auditLinks'
import { AuditLink } from '@/components/audit/AuditLink'
import {
  linkSource,
  productSource,
  storageSource,
  subjectSource,
  submissionSource,
} from '@/lib/auditTrailSources'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors } from '@/lib/tableSort'
import { ColumnCells, ResizableTableHead } from '@/components/table'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'

/**
 * Per-user column layout for the audit trail — and every column starts ON.
 *
 * The first cut hid five of the eighteen behind the Columns menu, on the
 * reasoning that nobody needs all of them at once and that the full set pushes
 * Related off the right edge. That reasoning was wrong for THIS table. An audit
 * trail is read to establish what happened, and a reader cannot know which
 * column holds the answer before they look — a hidden one does not read as
 * "available from a menu", it reads as a fact the system did not record. The
 * horizontal scroll is a much smaller cost than that doubt.
 *
 * The menu stays, so anyone who wants a narrower view can still make one. It
 * just no longer decides what a first-time reader sees.
 *
 * Date & time remains `locked`: a trail row that cannot say when it happened is
 * not an audit record.
 */
/**
 * Re-keyed when every column became visible by default.
 *
 * Stored prefs are the whole truth once a user has opened a table
 * (`seedDefaultHidden` only seeds on first sight), so changing the defaults
 * alone would have left the five columns hidden for exactly the people who had
 * already opened the trail and reported the problem. The id change is what
 * makes the new default reach them.
 *
 * The cost is real — it discards saved layouts for this table — and is only
 * acceptable because the table is one release old on dev and nobody has had
 * time to build a layout worth keeping. Do NOT reach for this again once it
 * has been out long enough for that to stop being true; migrate the stored
 * `hidden` list instead.
 */
const AUDIT_TABLE_ID = 'audit-trail-all-columns-v2'

export const AUDIT_COLUMNS: TableColumnDef[] = [
  // Widths sized to what each column actually holds (z8nrz7dq6q follow-up):
  // short values never wrap, and the free-text columns get room for two lines.
  { id: 'changedAt', header: 'Date & time', locked: true, defaultWidth: 175, sortable: true },
  { id: 'source', header: 'Source', defaultWidth: 95, sortable: true },
  { id: 'transactionType', header: 'Type', defaultWidth: 95, sortable: true },
  { id: 'changeType', header: 'Change type', defaultWidth: 125, sortable: true },
  { id: 'changedBy', header: 'Changed by', defaultWidth: 190, sortable: true },
  { id: 'din', header: 'DIN', defaultWidth: 140, sortable: true },
  { id: 'itemChanged', header: 'Item changed', defaultWidth: 160, sortable: true },
  { id: 'oldValue', header: 'Old value', defaultWidth: 180, sortable: true },
  { id: 'newValue', header: 'New value', defaultWidth: 180, sortable: true },
  { id: 'storageNode', header: 'Storage node', defaultWidth: 190, sortable: true },
  { id: 'reason', header: 'Reason', defaultWidth: 180, sortable: true },
  { id: 'description', header: 'Description', defaultWidth: 280, sortable: true },
  // Related is the point of the linking design — a row that touched storage
  // names the storage record AND that trail's own row.
  { id: 'links', header: 'Related', defaultWidth: 210 },
  // Ordered last because they are read least often, NOT hidden: see above.
  { id: 'transactionId', header: 'Transaction', defaultWidth: 115, sortable: true },
  { id: 'subjectKey', header: 'MRN / KIT / WO', defaultWidth: 170, sortable: true },
  { id: 'productCode', header: 'Product code', defaultWidth: 130, sortable: true },
  { id: 'submissionId', header: 'Submission', defaultWidth: 115, sortable: true },
  { id: 'dynamicData', header: 'Data', defaultWidth: 200 },
]

/** A value cell's text, or nothing — so a dash sorts last rather than first. */
function auditValueText(value: unknown): string | null {
  const text = renderAuditValue(value)
  return text === '—' ? null : text
}

/**
 * Column sorting (z8nrz7e4h6) — over the rows the caller hands in, whose own
 * order (newest first) is what "unsorted" returns to. Related and Data are
 * free-form structures with no order a reader would expect, so they stay put.
 */
const AUDIT_SORT: SortAccessors<AuditTrailRow> = {
  changedAt: e => e.changedAt,
  source: e => auditSourceLabel(e.source),
  transactionType: e => e.transactionType,
  changeType: e => e.changeType,
  changedBy: e => auditActorLabel(e),
  din: e => e.din,
  itemChanged: e => e.itemChanged,
  oldValue: e => auditValueText(e.oldValue),
  newValue: e => auditValueText(e.newValue),
  storageNode: e => {
    const storage = auditStorageNode(e)
    return storage.label || storage.id
  },
  reason: e => e.reason,
  description: e => e.description,
  transactionId: e => e.transactionId,
  subjectKey: e => (e.subjectKey ? `${e.subjectKeyType ?? ''} ${e.subjectKey}`.trim() : null),
  productCode: e => e.productCode,
  submissionId: e => e.submissionId,
}

const CHANGE_TYPE_STYLE: Record<string, string> = {
  transplant: 'bg-blue-100 text-blue-800',
  kit: 'bg-purple-100 text-purple-800',
  mes: 'bg-amber-100 text-amber-800',
  cln: 'bg-teal-100 text-teal-800',
  general: 'bg-slate-100 text-slate-700',
}

/**
 * Free text — a description, a value, a reason — in a column of fixed width,
 * wrapped to at most two lines. The whole text stays on hover, so nothing is
 * lost; the table just stops growing a row ten lines tall for one long value.
 */
function Clamp({ text, width, className }: { text: string; width: string; className?: string }) {
  return (
    <div className={`${width} line-clamp-2 whitespace-normal break-words ${className ?? ''}`} title={text}>
      {text}
    </div>
  )
}

/** Short values (dates, ids, types, names) never wrap. */
const NOWRAP = 'whitespace-nowrap text-xs'

/** One row's cells, keyed by column id. */
function auditCells(e: AuditTrailRow): Record<string, React.ReactNode> {
  const storage = auditStorageNode(e)
  return {
    transactionId: (
      <TableCell className='whitespace-nowrap font-mono text-xs text-muted-foreground' title={e.transactionId}>
        {/* Optional chaining, not confidence in the type: this table renders
            whatever the endpoint returned, and a row from a server that has
            not yet shipped the unified feed would otherwise take the whole
            panel down on a column nobody has open by default. */}
        {e.transactionId?.slice(0, 8) ?? '—'}
      </TableCell>
    ),
    changedAt: (
      <TableCell className='whitespace-nowrap text-xs'>
        {new Date(e.changedAt).toLocaleString()}
      </TableCell>
    ),
    source: <TableCell className={NOWRAP}>{auditSourceLabel(e.source)}</TableCell>,
    transactionType: <TableCell className={`${NOWRAP} capitalize`}>{e.transactionType}</TableCell>,
    changeType: (
      <TableCell className='whitespace-nowrap'>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
            CHANGE_TYPE_STYLE[e.changeType] ?? CHANGE_TYPE_STYLE.general
          }`}
        >
          {e.changeType}
        </span>
      </TableCell>
    ),
    // The email, not the id — but the stored value stays reachable on hover,
    // because that is the identity the record actually holds.
    changedBy: (
      <TableCell className={NOWRAP} title={e.changedBy}>
        {auditActorLabel(e)}
      </TableCell>
    ),
    // The TYPE stays plain — it labels the column, it is not the record. Only
    // the key itself is the reference, and only some kinds of key name
    // something reachable (see subjectTarget).
    subjectKey: (
      <TableCell className={NOWRAP}>
        {e.subjectKey ? (
          <>
            <span className='uppercase text-muted-foreground'>{e.subjectKeyType}</span>{' '}
            <AuditLink target={subjectTarget(e)} source={subjectSource(e)}>{e.subjectKey}</AuditLink>
          </>
        ) : (
          <span className='text-muted-foreground'>—</span>
        )}
      </TableCell>
    ),
    // Both name the same bag, so both go to the same place. A row with no
    // `productId` — a form edit that produced nothing — renders as before.
    din: (
      <TableCell className='whitespace-nowrap font-mono text-xs'>
        {e.din ? <AuditLink target={productTarget(e)} source={productSource(e)}>{e.din}</AuditLink> : '—'}
      </TableCell>
    ),
    productCode: (
      <TableCell className='whitespace-nowrap font-mono text-xs'>
        {e.productCode ? (
          <AuditLink target={productTarget(e)} source={productSource(e)}>
            {e.productCode}
          </AuditLink>
        ) : (
          '—'
        )}
      </TableCell>
    ),
    itemChanged: (
      <TableCell className='text-xs'>
        <Clamp text={e.itemChanged || '—'} width='w-36' />
      </TableCell>
    ),
    oldValue: (
      <TableCell className='text-xs text-muted-foreground'>
        <Clamp text={renderAuditValue(e.oldValue)} width='w-40' />
      </TableCell>
    ),
    newValue: (
      <TableCell className='text-xs'>
        <Clamp text={renderAuditValue(e.newValue)} width='w-40' />
      </TableCell>
    ),
    // The path is what a person reads; the id is what they quote in a ticket,
    // so it is the tooltip rather than the cell.
    storageNode: (
      <TableCell className={NOWRAP}>
        {storage.label || storage.id ? (
          <AuditLink target={storageTarget(e)} source={storageSource(e)} title={storage.id ?? undefined}>
            {storage.label || storage.id}
          </AuditLink>
        ) : (
          <span className='text-muted-foreground'>—</span>
        )}
      </TableCell>
    ),
    reason: (
      <TableCell className='text-xs'>
        <Clamp text={e.reason || '—'} width='w-40' />
      </TableCell>
    ),
    description: (
      <TableCell className='text-xs text-muted-foreground'>
        <Clamp text={e.description || '—'} width='w-64' />
      </TableCell>
    ),
    links: (
      <TableCell className='w-52 max-w-52 text-xs'>
        {e.links && e.links.length > 0 ? (
          <div className='space-y-0.5'>
            {e.links.map(l => (
              <div key={l.id} className='flex items-start gap-1'>
                <Link2 className='mt-0.5 h-3 w-3 shrink-0 text-muted-foreground' />
                <div className='min-w-0'>
                  <div className='truncate'>
                    <AuditLink target={linkTarget(l)} source={linkSource(l)} title={l.entityId}>
                      {linkLabel(l)}
                    </AuditLink>
                  </div>
                  {/* Stacked, not inline: the trail name is long and inline it
                      clipped off the right edge of the column. */}
                  {l.relatedAuditTable && (
                    <div
                      className='truncate text-[10px] text-muted-foreground'
                      title={`${l.relatedAuditTable} row ${l.relatedAuditId}`}
                    >
                      {l.relatedAuditTable}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <span className='text-muted-foreground'>—</span>
        )}
      </TableCell>
    ),
    // Truncated to eight characters, as before — the full id stays on the
    // tooltip, which is what somebody quoting it in a ticket needs.
    submissionId: (
      <TableCell className='whitespace-nowrap font-mono text-xs text-muted-foreground'>
        {e.submissionId ? (
          <AuditLink target={submissionTarget(e)} source={submissionSource(e)} title={e.submissionId}>
            {e.submissionId.slice(0, 8)}
          </AuditLink>
        ) : (
          '—'
        )}
      </TableCell>
    ),
    dynamicData: (
      <TableCell className='text-xs text-muted-foreground'>
        <Clamp text={e.dynamicData ? JSON.stringify(e.dynamicData) : '—'} width='w-44' />
      </TableCell>
    ),
  }
}

/**
 * The audit trail table.
 *
 * Shared by the panel under a form and the per-product dialog so a column
 * added for one cannot go missing on the other — the two views differ only in
 * what they are given, never in how a row reads.
 */
export function AuditTrailTable<R extends AuditTrailRow = AuditTrailRow>({
  rows,
  isLoading,
  isError = false,
  emptyMessage,
  tableId = AUDIT_TABLE_ID,
  columns = AUDIT_COLUMNS,
  maxHeightClass = 'max-h-[28rem]',
  extraCells,
}: {
  rows: R[]
  isLoading?: boolean
  /** The rows could not be loaded: said so, never shown as the empty message. */
  isError?: boolean
  emptyMessage: string
  /** Its own id gives a container a column layout of its own. */
  tableId?: string
  /** Defaults differ by container width — see NARROW_AUDIT_COLUMNS. */
  columns?: TableColumnDef[]
  maxHeightClass?: string
  /**
   * Cells for columns this table does not define — the Audit Trail Center's
   * "Audit trail" and "Record" (z8nrz7dq6q). Keyed by column id, like the rest.
   */
  extraCells?: (row: R) => Record<string, ReactNode>
}) {
  const cols = useTableColumns(tableId, columns)
  const sortedRows = useSortedRows(rows, cols.sort, AUDIT_SORT)

  // No toolbar: ResizableTableHead places the Columns menu in the last visible
  // header cell itself (see components/table/index.ts). A separate control
  // above the table would be a second, competing entry point.
  return (
    <div className={`${maxHeightClass} overflow-auto rounded-lg border`}>
      {/* Natural width, scrolling sideways: eighteen columns crushed into the
          container is what made every header and value wrap. */}
      <Table className='w-max min-w-full'>
        <TableHeader>
          <TableRow>
            {cols.visible.map(({ def }) => (
              <ResizableTableHead
                key={def.id}
                columnId={def.id}
                columns={cols}
                label={def.header}
                className='whitespace-nowrap'
              >
                {def.header}
              </ResizableTableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={cols.visible.length}
                className='py-6 text-center text-sm text-muted-foreground'
              >
                Loading…
              </TableCell>
            </TableRow>
          ) : isError ? (
            <TableRow>
              <TableCell
                colSpan={cols.visible.length}
                className='py-6 text-center text-sm text-muted-foreground'
              >
                The audit trail could not be loaded. Refresh the page to try again.
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={cols.visible.length}
                className='py-6 text-center text-sm text-muted-foreground'
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map(e => (
              <TableRow key={`${e.source}:${e.id}`}>
                <ColumnCells columns={cols} cells={extraCells ? { ...auditCells(e), ...extraCells(e) } : auditCells(e)} />
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

/**
 * The panel under a form used to get a reduced column set of its own. It does
 * not any more — the narrow container was the reason to hide columns, and
 * hiding them was the thing being complained about. Both views now show
 * everything and scroll; they keep separate `tableId`s only so a layout chosen
 * in the wide dialog does not narrow the panel, and vice versa.
 */
